import React, { useContext } from "react";
import {
  createStyles,
  Drawer,
  ScrollArea,
  Stack,
  Box,
  Group,
  ActionIcon,
} from "@mantine/core";
import { X } from "lucide-react";
import { Navigate } from "react-router-dom";
import { APP_ROUTES, NavigationLinkProps } from "../../providers/Router";
import { AuthContext } from "../../providers/AuthProvider";
import UserAccessControlUtils, {
  FEATURE_TIME_AND_ATTENDANCE,
} from "../../utils/UserAccessControlUtils";
import { useEnabledFeatureFlag as isEnabledFeatureFlag } from "../../common/feature-flagging/FeatureFlagProvider";
import UserCard from "../elements/UserCard";
import NavLink from "./NavLink";
import JemLogo from "../../common/assets/logo.webp";

interface MobileSideNavDrawerProps {
  opened: boolean;
  onClose: () => void;
}

const useStyles = createStyles((theme, _params, getRef) => {
  const icon = getRef("icon");
  return {
    drawer: {
      backgroundColor: "var(--pink-50)",
    },
    link: {
      ...theme.fn.focusStyles(),
      display: "flex",
      alignItems: "center",
      textDecoration: "none",
      fontSize: theme.fontSizes.sm,
      color: "var(--greyscale-text-caption)",
      padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
      borderRadius: theme.radius.sm,
      fontWeight: 500,
      minHeight: 44,

      "&:hover": {
        backgroundColor: "var(--pink-100)",
        color: "var(--greyscale-text-title)",
        [`& .${icon}`]: {
          color: "var(--greyscale-text-title)",
        },
      },
    },
    linkIcon: {
      ref: icon,
      color: "var(--greyscale-text-caption)",
      marginRight: theme.spacing.xs,
      flexShrink: 0,
    },
    linkActive: {
      "&&, &&:hover": {
        backgroundColor: "var(--pink-100)",
        color: "var(--greyscale-text-title)",
        [`& .${icon}`]: {
          color: "var(--greyscale-text-title)",
        },
      },
    },
    footer: {
      paddingTop: theme.spacing.md,
      marginTop: theme.spacing.md,
      borderTop: `1px solid var(--pink-200)`,
    },
    linkActionIcon: {
      width: 44,
      height: 44,
      borderRadius: theme.radius.md,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    activeActionIcon: {
      backgroundColor: "var(--pink-100)",
      color: "var(--greyscale-text-title)",
    },
    chevron: {},
  };
});

const isTimeAttendanceLink = (link: NavigationLinkProps): boolean =>
  link.requiredFeature === FEATURE_TIME_AND_ATTENDANCE ||
  link.path?.startsWith("/time-attendance") === true;

const MobileSideNavDrawer: React.FC<MobileSideNavDrawerProps> = ({
  opened,
  onClose,
}) => {
  const { classes, cx } = useStyles();
  const auth = useContext(AuthContext);

  if (!auth) {
    return <Navigate to="/" replace />;
  }

  const getProtectedNavigationLinks = (): NavigationLinkProps[] =>
    APP_ROUTES.filter((route) => {
      if (route.isFeatureFlagged && route.requiredFeatureFlag) {
        return isEnabledFeatureFlag(route.requiredFeatureFlag);
      }
      return true;
    })
      .filter((route) =>
        UserAccessControlUtils.hasFeature(
          auth.user_features,
          route.requiredFeature
        )
      )
      .filter((route) =>
        UserAccessControlUtils.hasPermission(
          auth.permissions,
          route.requiredPermissions
        )
      )
      .filter((route) => {
        if (!route.requiredCapability) return true;
        return UserAccessControlUtils.hasCapability(
          auth.ta_capabilities,
          route.requiredCapability
        );
      })
      .map((route) => ({
        ...route,
        path: route.path.replace(/\/:.*?\?/g, ""),
        children: route.children?.filter((child) => {
          if (child.isFeatureFlagged && child.requiredFeatureFlag) {
            return isEnabledFeatureFlag(child.requiredFeatureFlag);
          }
          return true;
        }),
      }));

  const allLinks = getProtectedNavigationLinks();
  // Phone-only: show Home + T&A. Hide other parent items until they're mobile-friendly.
  const mobileLinks = allLinks.filter(
    (link) => link.path === "/" || isTimeAttendanceLink(link)
  );

  const handleNavigate = (): void => {
    onClose();
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="100%"
      withCloseButton={false}
      closeOnEscape
      closeOnClickOutside
      trapFocus
      padding={0}
      styles={{
        drawer: { backgroundColor: "var(--pink-50)" },
      }}
      aria-label="Main menu"
    >
      <Stack
        spacing={0}
        style={{ height: "100%", padding: "var(--spacing-sm)" }}
      >
        <Group
          position="apart"
          className="border-b border-secondary-pink-200 pb-xs"
        >
          <a href="/" title="Go to Homepage">
            <img src={JemLogo} style={{ width: 64 }} alt="Jem Logo" />
          </a>
          <ActionIcon
            onClick={onClose}
            aria-label="Close menu"
            size="lg"
            sx={{ width: 44, height: 44 }}
          >
            <X size={20} />
          </ActionIcon>
        </Group>
        <ScrollArea style={{ flex: 1 }} scrollbarSize={6}>
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              paddingTop: 20,
              paddingBottom: 20,
            }}
          >
            {mobileLinks.map((link) => (
              <NavLink
                key={link.id}
                link={link}
                setCurrentPath={handleNavigate}
                classes={classes}
                cx={cx}
                expanded
                showIcon
              />
            ))}
          </Box>
        </ScrollArea>
        <Box className={classes.footer}>
          <UserCard auth={auth} displayText onNavigate={onClose} />
        </Box>
      </Stack>
    </Drawer>
  );
};

export default MobileSideNavDrawer;
